import React, { useCallback } from 'react';
import { Droppable, type DragItem } from '../dnd';
import { PlacedInputChip } from './PlacedInputChip';
import { applyDropToInput } from './chip-drop';
import type { ValidationStatus } from './types';
import { useSound } from '../sounds';

export interface InlineInputProps {
  widgetId: string;
  value: string;
  isDropped?: boolean;
  validationStatus?: ValidationStatus;
  inputType: 'numeric' | 'string';
  length?: number;
  digits?: number;
  placeholder?: string;
  onChange: (widgetId: string, value: string, fromDrop?: boolean, sourceOptionId?: string) => void;
  onFocus?: (widgetId: string) => void;
}

/**
 * Inline input widget - accepts both typed input and drag-drop values
 * When a value is dropped, it shows as a removable chip
 * Otherwise shows a regular text input
 */
export const InlineInput: React.FC<InlineInputProps> = ({
  widgetId,
  value,
  isDropped = false,
  validationStatus = null,
  inputType,
  length,
  digits,
  placeholder,
  onChange,
  onFocus,
}) => {
  const playSound = useSound();
  const handleChange = useCallback((newValue: string, fromDrop = false, sourceOptionId?: string) => {
    onChange(widgetId, newValue, fromDrop, sourceOptionId);
  }, [onChange, widgetId]);

  // Accepts fresh bank chips (drag-option) or chips moved from another input (placed-chip).
  const handleDrop = useCallback((item: DragItem) => {
    return applyDropToInput(widgetId, item, onChange);
  }, [onChange, widgetId]);

  const expectedLength = inputType === 'numeric' ? digits : length;
  const dynamicWidth = `${(expectedLength || 5) * 0.8 + 1}em`;
  const statusClass = validationStatus ? ` widget--${validationStatus}` : '';

  return (
    <Droppable
      id={widgetId}
      accepts={['drag-option', 'placed-chip']}
      onDrop={handleDrop}
      className="inline-widget inline-input-droppable"
    >
      {isDropped && value !== '' ? (
        // Show as chip only when value came from drag-drop
        <PlacedInputChip
          widgetId={widgetId}
          value={value}
          className={statusClass.trim()}
          onRemove={() => handleChange('', false)}
        />
      ) : (
        // Regular input for typing
        <input
          id={widgetId + '-input'}
          type="text"
          className={`inline-input${statusClass}`}
          value={value}
          onChange={(e) => handleChange(e.target.value, false)}
          onMouseDown={() => playSound('input-select')}
          onFocus={() => onFocus?.(widgetId)}
          maxLength={expectedLength}
          style={{ width: dynamicWidth }}
          placeholder={placeholder}
        />
      )}
    </Droppable>
  );
};
